import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface OrgMembership {
  id: string;
  organization_id: string;
  role: string;
  is_default: boolean;
  joined_at: string;
  last_accessed_at: string | null;
  organization: {
    id: string;
    name: string;
    type: string;
    email: string | null;
    phone: string | null;
    address: string | null;
    user_count: number;
    licence_id: string | null;
  };
}

interface ActiveOrgContextValue {
  memberships: OrgMembership[];
  activeOrg: OrgMembership | null;
  setActiveOrgId: (orgId: string) => void;
  clearActiveOrg: () => void;
  loading: boolean;
  /** Observer mode for superadmins */
  isObserverMode: boolean;
  observeOrg: (orgId: string) => void;
  stopObserving: () => void;
}

const ActiveOrgContext = createContext<ActiveOrgContextValue | null>(null);

export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() => {
    try { return localStorage.getItem("smart-efi-active-org"); } catch { return null; }
  });
  const [observingOrgId, setObservingOrgId] = useState<string | null>(null);

  // Fetch user's org memberships
  const { data: memberships = [], isLoading } = useQuery({
    queryKey: ["org-memberships", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_members")
        .select("*, organization:organizations(*)")
        .eq("user_id", user!.id)
        .order("last_accessed_at", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as OrgMembership[];
    },
    enabled: !!user,
  });

  // For superadmin observer mode - fetch ALL orgs (also prefetched by Hub admin tab)
  const { data: allOrgs = [], isLoading: allOrgsLoading } = useQuery({
    queryKey: ["all-orgs-admin"],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("organizations")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!observingOrgId,
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });

  // Validate persisted org against memberships
  useEffect(() => {
    if (!isLoading && memberships.length > 0 && activeOrgId && !observingOrgId) {
      const found = memberships.find(m => m.organization_id === activeOrgId);
      if (!found) {
        setActiveOrgIdState(null);
        try { localStorage.removeItem("smart-efi-active-org"); } catch {}
      }
    }
  }, [memberships, isLoading, activeOrgId, observingOrgId]);

  const setActiveOrgId = useCallback((orgId: string) => {
    setActiveOrgIdState(orgId);
    setObservingOrgId(null);
    try { localStorage.setItem("smart-efi-active-org", orgId); } catch {}
    // Update last_accessed_at via SECURITY DEFINER RPC (column-scoped, anti-escalation)
    if (user) {
      supabase.rpc("touch_org_membership_access", { p_org_id: orgId }).then(() => {});
    }
  }, [user]);

  const clearActiveOrg = useCallback(() => {
    setActiveOrgIdState(null);
    setObservingOrgId(null);
    try { localStorage.removeItem("smart-efi-active-org"); } catch {}
  }, []);

  const observeOrg = useCallback((orgId: string) => {
    setObservingOrgId(orgId);
    setActiveOrgIdState(null);
  }, []);

  const stopObserving = useCallback(() => {
    setObservingOrgId(null);
  }, []);

  // Build the active org from memberships or observer mode
  const activeOrg = useMemo<OrgMembership | null>(() => {
    if (observingOrgId) {
      const observedOrg = allOrgs.find((o: any) => o.id === observingOrgId);
      if (observedOrg) {
        return {
          id: "observer",
          organization_id: observingOrgId,
          role: "superadmin",
          is_default: false,
          joined_at: "",
          last_accessed_at: null,
          organization: observedOrg,
        };
      }
      return null;
    }
    if (activeOrgId) {
      return memberships.find(m => m.organization_id === activeOrgId) || null;
    }
    return null;
  }, [observingOrgId, allOrgs, activeOrgId, memberships]);

  const value = useMemo<ActiveOrgContextValue>(() => ({
    memberships,
    activeOrg,
    setActiveOrgId,
    clearActiveOrg,
    loading: isLoading || (!!observingOrgId && allOrgsLoading),
    isObserverMode: !!observingOrgId,
    observeOrg,
    stopObserving,
  }), [memberships, activeOrg, setActiveOrgId, clearActiveOrg, isLoading, observingOrgId, allOrgsLoading, observeOrg, stopObserving]);

  return (
    <ActiveOrgContext.Provider value={value}>
      {children}
    </ActiveOrgContext.Provider>
  );
}

const FALLBACK_ACTIVE_ORG: ActiveOrgContextValue = {
  memberships: [],
  activeOrg: null,
  setActiveOrgId: () => {},
  clearActiveOrg: () => {},
  loading: false,
  isObserverMode: false,
  observeOrg: () => {},
  stopObserving: () => {},
};

/**
 * Returns the active org context. Returns a stable fallback (instead of
 * throwing) when used outside the provider — this avoids violating the
 * Rules of Hooks in callers that wrap this in try/catch.
 */
export function useActiveOrg() {
  const ctx = useContext(ActiveOrgContext);
  return ctx ?? FALLBACK_ACTIVE_ORG;
}
